import {
  Button,
  Input,
  Modal,
  mqUntil,
  Text,
  Title,
  useModal,
} from "@biesse-group/react-components";
import * as Sentry from "@sentry/gatsby";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import styled, { css } from "styled-components";

import { DataLayerEvent, getHashedString, useDataLayer } from "../../../hooks/data-layer";
import { useReCaptchaButton } from "../../../hooks/forms/useReCaptchaButton";
import { useRevalidateOnError } from "../../../hooks/forms/useRevalidateOnError";
import { useLabels } from "../../../hooks/useLabels";
import { emailValidationRegex, mobileValidationRegex } from "../../../utils/forms";
import { useLayoutContext } from "../../Layout/layoutContext";
import { CountrySelect } from "../CountrySelect";
import { FormButtons } from "../FormButtons";
import { GdprCheckboxes } from "../GdprCheckboxes";
import { ModalOutcome } from "../GenericModalForm/ModalOutcome";
import { RequestStatus } from "../GenericModalForm/request-status";
import { ModalFormContainer } from "../ModalFormContainer";
import { FormPayload } from "../models/form-payload";
import { Divider } from "../utils";
import { sendForm } from "../utils/sendForm";

const StyledForm = styled.form`
  margin-top: 20px;
  margin-bottom: 30px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 35px;
  row-gap: 15px;

  ${mqUntil(
    `sm`,
    css`
      grid-template-columns: 1fr;
    `
  )}
`;

const StyledButton = styled(Button)`
  ${mqUntil(
    "sm",
    css`
      font-size: 12px;
    `
  )}
`;

export interface DownloadCatalogFormProps {
  buttonLabel?: string;
  catalogName: string;
  catalogId: string;
  fileName: string;
  onOpenModal?: () => void;
}

export const DownloadCatalogForm: FC<DownloadCatalogFormProps> = ({
  buttonLabel,
  catalogName,
  catalogId,
  fileName,
  onOpenModal,
}) => {
  const { language } = useLayoutContext();

  const [selectedNation, setSelectedNation] = useState<Queries.CountriesStatesCitiesJson>();
  const [requestStatus, setRequestStatus] = useState(RequestStatus.INITIAL);

  const defaultFormValues = useMemo<FormPayload>(
    () => ({
      requestMessage: `Download catalogue ${fileName}`,
      firstName: "",
      lastName: "",
      company: "",
      email: "",
      locale: language,
      consents: {
        privacy: false,
        marketing: false,
        communication: false,
      },
    }),
    [language, fileName]
  );

  const {
    control,
    watch,
    trigger,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormPayload>({
    defaultValues: defaultFormValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  useRevalidateOnError(trigger, errors, watch);

  const labels = useLabels([
    "info-form-name",
    "info-form-surname",
    "download-catalog-form-title",
    "download-catalog-form-description",
    "download-catalog-form-cancel",
    "download-catalog-form-confirm",
    "download-catalog-button",
    "download-catalog-success",
    "info-form-email",
    "info-form-company",
    "info-form-mobile",
  ]);

  // TODO: localize this label if we actually want to show it as an error message
  // (right now it's not shown, inputs in error only have a red border)
  const requiredFieldLabel = "Mandatory field";

  const { pushEvent } = useDataLayer();
  const email = watch("email");
  const sendDownloadEvent = useCallback(() => {
    const hashedUserEmail = getHashedString(email);
    const event: DataLayerEvent = {
      event: "download_catalogue",
      user_email_md5: hashedUserEmail.md5,
      user_email_sha256: hashedUserEmail.sha256,
      catalogue_name: catalogName,
    };
    pushEvent(event);
  }, [email, pushEvent, catalogName]);

  const { isOpen, close, open } = useModal({
    onOpen: onOpenModal,
  });

  const { handleReCaptchaVerify, isDisabled: isReCaptchaNotReady } = useReCaptchaButton();

  const onSubmit = useCallback(
    async (data: FormPayload) => {
      setRequestStatus(RequestStatus.PENDING);
      const token = await handleReCaptchaVerify();

      try {
        await sendForm({ type: "catalogs", data, token, catalogId });
        setRequestStatus(RequestStatus.SUCCESS);
      } catch (err) {
        console.error(err);
        setRequestStatus(RequestStatus.ERROR);

        if (!process.env.EXCLUDE_SENTRY) {
          Sentry.captureException(err);
        }
      } finally {
        sendDownloadEvent();
      }
    },
    [handleReCaptchaVerify, sendDownloadEvent, catalogId]
  );

  useEffect(() => {
    if (!isOpen) {
      reset(defaultFormValues);
      setRequestStatus(RequestStatus.INITIAL);
    } else if (onOpenModal) {
      onOpenModal();
    }
  }, [isOpen, onOpenModal, reset, defaultFormValues]);

  return (
    <>
      <StyledButton type="button" variant="primary-inverted" onClick={open} rightIcon="download">
        {buttonLabel || labels["download-catalog-button"]}
      </StyledButton>
      <Modal isOpen={isOpen} close={close}>
        {requestStatus !== RequestStatus.INITIAL ? (
          <ModalOutcome
            requestStatus={requestStatus}
            successMessage={labels["download-catalog-success"] ?? ""}
            onClose={close}
          />
        ) : (
          <ModalFormContainer>
            <Title color="light" style={{ marginBottom: "10px" }} variant="h3">
              {labels["download-catalog-form-title"]}
            </Title>
            <Text color="light">{labels["download-catalog-form-description"]}</Text>
            <StyledForm>
              <Controller
                control={control}
                name="firstName"
                rules={{ required: requiredFieldLabel }}
                render={({ field, fieldState }) => (
                  <Input
                    error={fieldState.error?.message}
                    type="text"
                    {...field}
                    placeholder={`${labels["info-form-name"]}*`}
                    shadow="dark"
                  />
                )}
              />
              <Controller
                control={control}
                name="lastName"
                rules={{ required: requiredFieldLabel }}
                render={({ field, fieldState }) => (
                  <Input
                    error={fieldState.error?.message}
                    type="text"
                    placeholder={`${labels["info-form-surname"]}*`}
                    {...field}
                    shadow="dark"
                  />
                )}
              />
              <Controller
                control={control}
                name="company"
                rules={{ required: requiredFieldLabel }}
                render={({ field, fieldState }) => (
                  <Input
                    type="text"
                    error={fieldState.error?.message}
                    placeholder={`${labels["info-form-company"]}*`}
                    {...field}
                    shadow="dark"
                  />
                )}
              />
              <Controller
                control={control}
                name="mobile"
                rules={{
                  required: requiredFieldLabel,
                  pattern: {
                    value: mobileValidationRegex,
                    message: "Please enter a valid phone number",
                  },
                }}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <Input
                    type="tel"
                    placeholder={`${labels["info-form-mobile"]}*`}
                    error={fieldState.error?.message}
                    shadow="dark"
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="email"
                rules={{
                  required: requiredFieldLabel,
                  pattern: {
                    value: emailValidationRegex,
                    message: "Please enter a valid email",
                  },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    type="email"
                    error={fieldState.error?.message}
                    placeholder={`${labels["info-form-email"]}*`}
                    shadow="dark"
                    {...field}
                  />
                )}
              />

              <Controller
                control={control}
                name="country"
                rules={{ required: requiredFieldLabel }}
                render={({ field: { onChange }, fieldState: { error } }) => (
                  <CountrySelect
                    value={selectedNation}
                    error={error?.message}
                    onChange={(country) => {
                      setSelectedNation(country);
                      onChange(country.iso3 || "");
                    }}
                  />
                )}
              />
            </StyledForm>
            <Divider />

            <GdprCheckboxes consentsFieldName="consents" control={control} />

            <FormButtons
              cancelButton={{
                label: labels["download-catalog-form-cancel"] || "",
                onClick: close,
              }}
              confirmButton={{
                label: labels["download-catalog-form-confirm"] || "",
                disabled: isReCaptchaNotReady,
                onClick: handleSubmit(onSubmit),
              }}
            />
          </ModalFormContainer>
        )}
      </Modal>
    </>
  );
};
